import { memo, useEffect, useMemo, useRef, useState } from 'react';
import Markdown from 'markdown-to-jsx';
import {
    UserMessage,
    AssistantMessage,
    Footer,
    Datasource,
    DatasourceChapter,
    DatasourceDivision,
    LoadingMessage,
} from './styled';
import Button from '../../../../common/Button';

const LOADING_MESSAGE = 'Bearbetar din fråga...';

type Props = {
    content: Message;
    isLatest: boolean;
    isLoading?: boolean;
    displayPdfPage: (targetPageNum: number) => void;
};

function MessageComponent(props: Props) {
    const { content, isLatest, isLoading, displayPdfPage } = props;
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const messageRef = useRef<HTMLDivElement>(null);
    const sourceRef = useRef<HTMLDivElement>(null);
    const elementId = useMemo(() => new Date().getTime().toString(), []);

    function openDatasources() {        
        setIsOpen((prev) => !prev);        
    }

    // Add this useEffect to handle scrolling after datasources render
    useEffect(() => {
        if (isOpen && sourceRef.current) {
            sourceRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [isOpen]); // Trigger when isOpen changes


    useEffect(() => {        
        if (isLatest && messageRef.current) {
            messageRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'start', // aligns the top of the element with the top of the scrollable container
            });
        }
    }, [isLatest]);

    const isGenerating = isLoading && !content.model;
    return (
        <>
            <UserMessage ref={messageRef}>{content.user}</UserMessage>
            <AssistantMessage $isLatest={isLatest}>
                {isGenerating && <LoadingMessage>{LOADING_MESSAGE}</LoadingMessage>}
                <Markdown className="markdown">{content.model}</Markdown>
                {content.datasources.length > 0 && (
                    <Footer id={elementId}>
                        <Button text="Datakällor" onClick={openDatasources} />
                        {isOpen &&
                            content.datasources.map((datasource, index) => {
                                const [chapter, ...subChapter] = datasource.title.split(' - ');
                                return (
                                    <Datasource
                                        key={datasource.title}
                                        onClick={() => displayPdfPage(datasource.page_num[0])}
                                        role="button"
                                        ref={index === content.datasources.length - 1 ? sourceRef : null}
                                    >
                                        <DatasourceDivision>{chapter}</DatasourceDivision>
                                        <DatasourceChapter>
                                            {subChapter.join('-').trim()}
                                        </DatasourceChapter>
                                    </Datasource>
                                );
                            })}
                    </Footer>
                )}
            </AssistantMessage>
        </>
    );
}

const MemoizedMessage = memo(MessageComponent, (prevProps: Props, nextProps: Props) => {
    return (
        prevProps.content.user === nextProps.content.user &&
        prevProps.content.model === nextProps.content.model &&
        prevProps.isLatest === nextProps.isLatest &&
        prevProps.isLatest === nextProps.isLoading &&
        JSON.stringify(prevProps.content.datasources) ===
            JSON.stringify(nextProps.content.datasources)
    );
});

export default MemoizedMessage;
